import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Lock, LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { translations } from '../mock';
import { useToast } from '../hooks/use-toast';

export function AuthCard({ role, title, redirect, hint, allowSignup = false, showAdminLink = false }) {
  const { login, register } = useAuth();
  const { t, lang } = useLang();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [mode, setMode] = useState('signin');
  const [form, setForm] = useState({ name: '', email: '', password: '' });

  const submit = async (e) => {
    e.preventDefault();
    const r = mode === 'signup'
      ? await register(form.name, form.email, form.password, role)
      : await login(form.email, form.password, role);
    if (r.ok) {
      toast({ title: lang === 'en' ? (mode === 'signup' ? 'Account created!' : `Welcome ${r.user.name}`) : (mode === 'signup' ? 'खाता बन गया!' : `नमस्ते ${r.user.name}`) });
      navigate(redirect);
    } else {
      toast({ title: r.error, variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-10 om-bg">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-orange-100 p-8 md:p-10">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-stone-900">{title}</h1>
        </div>
        <form onSubmit={submit} className="space-y-4">
          {mode === 'signup' && (
            <div>
              <label className="text-sm font-medium text-stone-700 mb-1 flex items-center gap-2"><User className="w-4 h-4" /> {t(translations.auth.name)}</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full border border-orange-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1 flex items-center gap-2"><Mail className="w-4 h-4" /> {t(translations.auth.email)}</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full border border-orange-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div>
            <label className="text-sm font-medium text-stone-700 mb-1 flex items-center gap-2"><Lock className="w-4 h-4" /> {t(translations.auth.password)}</label>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="w-full border border-orange-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <button type="submit" className="btn-orange w-full rounded-lg py-3 font-semibold flex items-center justify-center gap-2">
            {mode === 'signin' ? <><LogIn className="w-4 h-4" /> {t(translations.auth.signIn)}</> : <><UserPlus className="w-4 h-4" /> {t(translations.auth.signUp)}</>}
          </button>
        </form>
        {allowSignup && (
          <div className="text-center mt-5 text-sm text-stone-600">
            {mode === 'signin' ? t(translations.auth.noAccount) : t(translations.auth.haveAccount)}{' '}
            <button onClick={() => setMode(mode === 'signin' ? 'signup' : 'signin')} className="text-orange-700 font-semibold hover:underline">
              {mode === 'signin' ? t(translations.auth.signUp) : t(translations.auth.signIn)}
            </button>
          </div>
        )}
        {showAdminLink && (
          <div className="mt-6 pt-4 border-t border-stone-200 text-center">
            <Link
              to="/admin/login"
              data-testid="admin-login-link"
              className="text-[11px] text-stone-400 hover:text-orange-600 tracking-wider uppercase"
            >
              {lang === 'hi' ? 'एडमिन लॉगिन' : 'Admin login'}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Login() {
  const { t } = useLang();
  return <AuthCard role="user" title={t(translations.auth.userLogin)} redirect="/dashboard" allowSignup showAdminLink />;
}
